 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useCallback, useEffect } from 'react';
import { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useUIStore } from '../stores';
import { tutorialSteps, TutorialStep } from '../tutorial/tutorialSteps';

interface TutorialState {
  isCompleted: boolean;
  isSkipped: boolean;
  currentStepId: string | null;
  stepIndex: number;
}

export function useTutorial() {
  const {
    tutorialStep,
    setTutorialStep,
    tutorialCompleted,
    tutorialSkipped,
    setTutorialCompleted,
    setTutorialSkipped
  } = useUIStore();

  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(tutorialStep || 0);

  // Sync with store
  useEffect(() => {
    if (tutorialStep !== null && tutorialStep !== stepIndex) {
      setStepIndex(tutorialStep);
    }
  }, [tutorialStep, stepIndex]);

  const tutorialState: TutorialState = {
    isCompleted: tutorialCompleted || false,
    isSkipped: tutorialSkipped || false,
    currentStepId: tutorialSteps[stepIndex]?.id || null,
    stepIndex
  };

  const startTutorial = useCallback(() => {
    setIsRunning(true);
    setStepIndex(0);
    setTutorialStep(0);
    setTutorialSkipped(false);
    setTutorialCompleted(false);
  }, [setTutorialStep, setTutorialSkipped, setTutorialCompleted]);

  const skipTutorial = useCallback(() => {
    setIsRunning(false);
    setTutorialSkipped(true);
    setTutorialStep(null);
  }, [setTutorialSkipped, setTutorialStep]);

  const completeTutorial = useCallback(() => {
    setIsRunning(false);
    setTutorialCompleted(true);
    setTutorialStep(null);
  }, [setTutorialCompleted, setTutorialStep]);

  const nextStep = useCallback(() => {
    const newIndex = Math.min(stepIndex + 1, tutorialSteps.length - 1);
    setStepIndex(newIndex);
    setTutorialStep(newIndex);
  }, [stepIndex, setTutorialStep]);

  const previousStep = useCallback(() => {
    const newIndex = Math.max(stepIndex - 1, 0);
    setStepIndex(newIndex);
    setTutorialStep(newIndex);
  }, [stepIndex, setTutorialStep]);

  const goToStep = useCallback((index: number) => {
    const newIndex = Math.max(0, Math.min(index, tutorialSteps.length - 1));
    setStepIndex(newIndex);
    setTutorialStep(newIndex);
  }, [setTutorialStep]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    // Handle status changes
    if (status === STATUS.FINISHED) {
      completeTutorial();
      return;
    }

    if (status === STATUS.SKIPPED) {
      skipTutorial();
      return;
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        const newIndex = index + 1;
        if (newIndex < tutorialSteps.length) {
          setStepIndex(newIndex);
          setTutorialStep(newIndex);
        }
      } else if (action === ACTIONS.PREV) {
        const newIndex = index - 1;
        if (newIndex >= 0) {
          setStepIndex(newIndex);
          setTutorialStep(newIndex);
        }
      }
    }

    // Handle close action
    if (action === ACTIONS.CLOSE) {
      skipTutorial();
    }
  }, [completeTutorial, skipTutorial, setTutorialStep]);

  const shouldSkipStep = useCallback((stepId: string): boolean => {
    // Add logic here to determine if a step should be skipped
    // For example, if certain conditions are met
    return false;
  }, []);

  const getNextUnskippedStep = useCallback((currentIndex: number): number => {
    let nextIndex = currentIndex + 1;
    while (nextIndex < tutorialSteps.length) {
      const step = tutorialSteps[nextIndex];
      if (!shouldSkipStep(step.id)) {
        return nextIndex;
      }
      nextIndex++;
    }
    return currentIndex;
  }, [shouldSkipStep]);

  const resetTutorial = useCallback(() => {
    setIsRunning(false);
    setStepIndex(0);
    setTutorialStep(null);
    setTutorialSkipped(false);
    setTutorialCompleted(false);
  }, [setTutorialStep, setTutorialSkipped, setTutorialCompleted]);

  return {
    tutorialState,
    isRunning,
    stepIndex,
    currentStep: tutorialSteps[stepIndex],
    totalSteps: tutorialSteps.length,
    startTutorial,
    skipTutorial,
    completeTutorial,
    nextStep,
    previousStep,
    goToStep,
    handleJoyrideCallback,
    shouldSkipStep,
    getNextUnskippedStep,
    resetTutorial
  };
}